/**
 * Shared rendering rules for wildfires on a Leaflet map.
 *
 * The wildfires index owned these inline; the forecast map now draws the same
 * burn areas, and two copies of the coordinate flip and the colour ramp would
 * drift apart the first time the palette changed.
 */

/** The subset of a `/wildfires/map` row these helpers care about. */
export interface WildfireMapShape {
  is_active?: boolean | null;
  review_status?: string | null;
  location?: { coordinates: [number, number] } | null;
  area_polygon?: { coordinates: [number, number][][] } | null;
}

export interface WildfireColors {
  stroke: string;
  fill: string;
}

/**
 * Burn-area ring as Leaflet positions, or null when the fire has no polygon.
 *
 * GeoJSON is [lng, lat]; Leaflet wants [lat, lng]. Only the outer ring is
 * used — the backend stores a single-ring polygon per fire.
 */
export function wildfirePolygonPositions(
  w: WildfireMapShape,
): Array<[number, number]> | null {
  const ring = w.area_polygon?.coordinates?.[0];
  if (!ring || ring.length === 0) return null;
  return ring.map(([lng, lat]) => [lat, lng] as [number, number]);
}

/** Marker position as Leaflet [lat, lng], or null when the fire has no point. */
export function wildfireMarkerPosition(
  w: WildfireMapShape,
): [number, number] | null {
  const coords = w.location?.coordinates;
  if (!coords || coords.length < 2) return null;
  const [lng, lat] = coords;
  return [lat, lng];
}

/** Drafts render muted grey; active = red, resolved = green. */
export function wildfireColors(w: WildfireMapShape): WildfireColors {
  const isDraft = w.review_status === 'draft';
  return {
    stroke: isDraft ? '#9e9e9e' : w.is_active ? '#e64a19' : '#2e7d32',
    fill: isDraft ? '#cfcfcf' : w.is_active ? '#ff7d47' : '#60ad5e',
  };
}

/** Path options for a burn-area polygon, matching the wildfires index. */
export function wildfirePolygonPathOptions(w: WildfireMapShape) {
  const { stroke, fill } = wildfireColors(w);
  return {
    color: stroke,
    fillColor: fill,
    fillOpacity: 0.4,
    weight: 2,
  };
}
